import React, {ReactElement} from "react";
import {Box, ButtonBase, Card, Tab, Tabs} from "@mui/material";
import {FocusMode, UserCache, UserProfilePhoto} from "./types";
import {BORDER_RADIUS, BORDER_RADIUS_MEDIUM, DIVIDER, PD_MD, PD_SM, SZ_MD, SZ_SM, SZ_SSM, TAB_SIZE} from "./dimens";
import {Link, Outlet} from "react-router-dom";
import {black, translucentBlack, translucentWhite, white} from "./colors";
import {BaseApp} from "./BaseApp";
import {UserFragment} from "./UserFragment";
import {TabInfo, TabsContainer, TabsContainerProps, TabsContainerState} from "./TabsContainer";
import {QuestionMarkOutlined} from "@mui/icons-material";
import {getAuth} from "@firebase/auth";
import {PluginHostFragment} from "./PluginHostFragment";
import {findIcon} from "./icons";
import {StyledContainer, StyledSpan} from "./StyledComponents";

const ICON_STYLE = {width: TAB_SIZE + 16, height: TAB_SIZE, padding: 0};

export type AppTabsContainerProps<TAB_TYPE> = TabsContainerProps<TAB_TYPE> & {
  logo?: string,
  noLogo?: boolean,
  noUserButton?: boolean,
}

export type AppTabsContainerState<TAB_TYPE> = TabsContainerState<TAB_TYPE> & {
  focusMode?: FocusMode,
}

export type AppTabsStyleFlags = {
  wallpaper?: string,
  contentMode?: "stretch" | "size", // Whether the tab's content is shown full-width "stretch" or a fixed "size".
  contentModeSize?: "xxlg" | "xlg" | "lg" | "md" | "sm",
  contentModeSizeStyle?: any,
}

export class AppTabsContainer<TAB_TYPE, P extends AppTabsContainerProps<TAB_TYPE>, S extends AppTabsContainerState<TAB_TYPE>> extends TabsContainer<TAB_TYPE, P, S> {

  protected readonly theme = BaseApp.CONTEXT.getAppConfig().theme;
  protected readonly auth = getAuth();

  componentDidMount() {
    this.fetchUser();
  }

  componentWillUnmount() {
  }

  protected styleFlags(): AppTabsStyleFlags | undefined {
    return undefined;
  }

  private async fetchUser() {
    const user = await UserCache.getInstance().getUser(this.auth.currentUser.uid);
    this.setState({
      user: user,
    });
  }

  render() {
    const styleFlags = this.styleFlags();
    const toolbar = this.renderToolbar();
    const borderRadius = this.theme.shape.borderRadius;
    return <Box style={{display: "flex", width: "100%", flexGrow: 1, overflow: "hidden"}}>
      {!this.state.focusMode
        ? <Box
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            flexShrink: 0,
            borderRadius: `0 ${borderRadius}px ${borderRadius}px 0`,
            overflow: "hidden",
            width: TAB_SIZE + 16,
            paddingLeft: 0,//8,
            paddingRight: 0,//8,
            background: this.props.tabsThemeColor || BaseApp.CONTEXT.getAppConfig()?.themeColor || this.theme.palette.primary.main,
            zIndex: 1200,
            position: "relative",
            borderRight: DIVIDER,
          }}>
          {!this.props.noLogo
            ? <ButtonBase
              onClick={() => this.props.path.navigate("/")}>
              {this.renderLogo()}
            </ButtonBase>
            : null}
          <Tabs
            orientation='vertical'
            indicatorColor="secondary"
            textColor="secondary"
            value={this.state.tabPath}
            onChange={(event, value) => this.setState({tabPath: value})}>
            {TabsContainer.expandGroups(this.props.tabs).items.filter(tab => !tab.hidden).map(tab => {
              return this.renderTabButton(tab, this.props.modulePath, this.props.tabsColorScheme);
            })}
          </Tabs>
          <StyledSpan/>
          {this.renderSecondaryButtons()}
          {!this.props.noUserButton
            ? <Box style={{display: "flex", alignItems: "center", gap: PD_SM, marginBottom: PD_MD}}>
              <Card style={{width: SZ_SM, height: SZ_SM, flexShrink: 0}}>
                <ButtonBase
                  onClick={() => BaseApp.CONTEXT.showDialog(
                    null,
                    () => <UserFragment uid={this.auth.currentUser.uid}/>)}>
                  <img src={UserProfilePhoto(UserCache.getInstance().getCachedUser(this.auth.currentUser.uid))}
                       style={{width: "100%", height: "100%"}}/>
                </ButtonBase>
              </Card>
            </Box>
            : null}
        </Box>
        : null}
      <Box style={{display: "flex", flexGrow: 1, flexDirection: "column", position: "relative"}}>
        {styleFlags?.wallpaper
          ? <img src={styleFlags.wallpaper}
                 style={{position: "absolute", width: "100%", height: "100%", objectFit: "cover", opacity: .75}}/>
          : null}
        {this.renderContentMode(<>
          {Boolean(toolbar) ? this.renderToolbarInContainer(toolbar) : null}
          <Outlet/>
          <PluginHostFragment hostId={BaseApp.CONTEXT.getAppConfig().name + "-" + this.props.tabs.containerId}/>
        </>)}
      </Box>
    </Box>;
  }

  private renderContentMode(children: ReactElement): ReactElement {
    const styleFlags = this.styleFlags();
    switch (styleFlags?.contentMode) {
      default:
      case "stretch":
        return children;
      case "size":
        return <div
          style={{display: "flex", flexDirection: "column", width: "100%", height: "100%", overflowY: "scroll"}}>
          <StyledContainer
            style={{padding: 0, flexGrow: 1, ...styleFlags?.contentModeSizeStyle}}
            size={styleFlags?.contentModeSize || "xxlg"}>
            <Card style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              flexGrow: 1,
              borderRadius: "8px 8px 0 0"
            }}>
              {children}
            </Card>
          </StyledContainer>
        </div>;
    }
  }

  private renderLogo(): ReactElement {
    const appConfig = BaseApp.CONTEXT.getAppConfig();
    const logo = this.props.logo || appConfig.logo || "@icon/extension";
    if (logo.endsWith(".png")) {
      return <img src={logo} style={{width: SZ_MD, height: SZ_MD}}/>;
    } else if (logo.startsWith("@icon/")) {
      const IconType = findIcon(logo.substring("@icon/".length));
      return <div style={{
        width: SZ_MD,
        height: SZ_MD,
        padding: "20%",
        borderRadius: BORDER_RADIUS_MEDIUM,
        // backgroundColor: theme.palette.primary.dark,
        color: "white"
      }}>
        <IconType style={{width: "100%", height: "100%",}}/>
      </div>;
    }
    return null;
  }

  protected renderSecondaryButtons(): ReactElement | null {
    return null;
  }

  private renderTabButton(tab: TabInfo<TAB_TYPE>, modulePath?: string, colorScheme?: "light" | "dark") {
    colorScheme = colorScheme || BaseApp.CONTEXT.getPreferredColorScheme();
    const colorSchemeLight = colorScheme === "light";
    const selected = tab.path === this.state.tabPath;
    return <Tab
      icon={this.renderTabIcon(tab)}
      label={tab.text}
      component={Link}
      to={[modulePath, tab.path].join("/")}
      style={{
        ...ICON_STYLE,
        fontSize: 9,
        opacity: selected ? 1 : (colorSchemeLight ? 1 : 0.5),
        background: selected ? (colorSchemeLight ? translucentWhite : translucentBlack) : null,
        border: selected && colorSchemeLight ? DIVIDER : null,
        color: colorSchemeLight ? black : white,
        minWidth: 0,
        borderRadius: this.theme.shape.borderRadius,
        alignItems: "center",
        justifyContent: "center",
      }}/>;
  }

  private renderTabIcon(tab: TabInfo<TAB_TYPE>): ReactElement {
    if (tab.iconType) {
      return <tab.iconType style={{width: 24, height: 24}}/>;
    } else if (tab.iconUrl) {
      return <img src={tab.iconUrl} style={{width: SZ_SSM, height: SZ_SSM, borderRadius: BORDER_RADIUS}}/>
    }
    return <QuestionMarkOutlined/>;
  }

  private renderToolbarInContainer(toolbar: ReactElement): ReactElement {
    return <Box style={{
      width: "100%",
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
      justifyContent: "center",
      borderBottom: DIVIDER,
      background: this.theme.palette.background.default,
    }}>
      {toolbar}
    </Box>;
  }

  protected renderToolbar(): ReactElement {
    return null;
  }
}
